import { useRef, useState } from "react";
import { Copy, Hash, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, inputCls } from "@/components/kit";
import { FloorMap } from "@/components/FloorMap";
import { API } from "@/lib/api";

const SHAPES = [["rotondo", "Rotondo"], ["rettangolare", "Rettangolare"], ["ovale", "Ovale"], ["quadrato", "Quadrato"]];

function TableForm({ t, set, remove }) {
  const inp = (k, type = "number", ph = "") => <input type={type} className={inputCls} value={t[k] ?? ""} placeholder={ph} onChange={(e) => set({ [k]: e.target.value })} data-testid={`tf-${k}`} />;
  return (
    <div className="space-y-3" data-testid="table-form">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Numero">{inp("numero", "text", "1, 2, A…")}</Field>
        <Field label="Forma"><select className={inputCls} value={t.forma} onChange={(e) => set({ forma: e.target.value })} data-testid="tf-forma">{SHAPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
        {t.forma === "rotondo" ? <Field label="Diametro (cm)">{inp("diametro")}</Field> : <><Field label={t.forma === "quadrato" ? "Lato (cm)" : "Lunghezza (cm)"}>{inp("lunghezza")}</Field>{t.forma !== "quadrato" && <Field label="Larghezza (cm)">{inp("larghezza")}</Field>}</>}
        <Field label="Posti">{inp("posti")}</Field>
        <Field label="Rotazione (°)">{inp("rot")}</Field>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#C9A96A]" checked={!!t.sposi} onChange={(e) => set({ sposi: e.target.checked })} data-testid="tf-sposi" /> Tavolo / postazione sposi</label>
      <Button size="sm" variant="ghost" className="text-[#991B1B] rounded-full" onClick={remove} data-testid="tf-remove"><Trash2 size={14} className="mr-1" /> Elimina tavolo</Button>
    </div>
  );
}

function NumberSeq({ seq, setSeq }) {
  const [start, setStart] = useState("1");
  if (seq !== null) return <div className="rounded-lg bg-[#EAF5F7] p-3 text-sm flex items-center justify-between gap-2" data-testid="seq-active"><span>Tocca i tavoli in ordine: il prossimo prende il <b>{seq}</b></span><Button size="sm" variant="outline" className="rounded-full" onClick={() => setSeq(null)} data-testid="seq-stop">Fine</Button></div>;
  return (
    <div className="flex gap-2 items-center">
      <input className={`${inputCls} w-20`} value={start} onChange={(e) => setStart(e.target.value)} data-testid="seq-start" />
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setSeq(parseInt(start, 10) || 1)} data-testid="seq-begin"><Hash size={14} className="mr-1" /> Numera toccando</Button>
    </div>
  );
}

export function TableEditor({ plan, tables, onChange, mapProps = {}, side, footer, testId = "plan-editor-map" }) {
  const [sel, setSel] = useState(null);
  const [seq, setSeq] = useState(null);
  const drag = useRef(null);
  const ref = useRef(null);
  const set = (id, p) => onChange((ts) => ts.map((t) => (t.id === id ? { ...t, ...p } : t)));
  const add = (base) => {
    const id = crypto.randomUUID();
    const last = tables.filter((x) => !x.sposi).at(-1);
    onChange((ts) => [...ts, { id, numero: "", forma: "rotondo", diametro: 180, lunghezza: 240, larghezza: 100, posti: 10, x: 50, y: 50, rot: 0, sposi: false, ...(last && !base ? { forma: last.forma, diametro: last.diametro, lunghezza: last.lunghezza, larghezza: last.larghezza, posti: last.posti, x: Math.min(last.x + 6, 97), y: Math.min(last.y + 6, 97) } : {}), ...base }]);
    setSel(id);
  };
  const tap = (e, x) => {
    e.preventDefault();
    if (mapProps.onMapClick) return;
    if (seq !== null) {
      onChange((ts) => ts.map((t) => (t.numero === String(seq) && t.id !== x.id ? { ...t, numero: "" } : t.id === x.id ? { ...t, numero: String(seq) } : t)));
      setSeq(seq + 1);
      return;
    }
    drag.current = x.id;
    setSel(x.id);
  };
  const move = (e) => {
    if (!drag.current || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    set(drag.current, { x: Math.round(((e.clientX - r.left) / r.width) * 1000) / 10, y: Math.round(((e.clientY - r.top) / r.height) * 1000) / 10 });
  };
  const t = tables.find((x) => x.id === sel);
  const seats = tables.reduce((s, x) => s + Number(x.posti || 0), 0);
  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 space-y-2">
        <FloorMap plan={plan} src={`${API}/floorplans/${plan.id}/image?v=${plan.updated_at}`} tables={tables} selected={sel} mapRef={ref} {...mapProps}
          onTablePointerDown={tap} onBgPointerMove={move} onBgPointerUp={() => { drag.current = null; }} testId={testId} />
        <p className="text-xs text-[#7B929C]" data-testid="editor-count">Trascina i tavoli sulla planimetria. {tables.length} tavoli • {seats} posti in tutto • in scala ({Number(plan.larghezza_m || 0).toLocaleString("it-IT")} m di larghezza).</p>
      </div>
      <div className="lg:col-span-4 space-y-4">
        {side}
        <NumberSeq seq={seq} setSeq={setSeq} />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="rounded-full" onClick={() => add()} data-testid="plan-add-table"><Plus size={14} className="mr-1" /> Tavolo</Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => add({ forma: "rettangolare", lunghezza: 300, larghezza: 80, posti: 2, sposi: true, y: 15 })} data-testid="plan-add-sposi">♥ Postazione sposi</Button>
          {t && <Button size="sm" variant="outline" className="rounded-full" onClick={() => { const id = crypto.randomUUID(); onChange((ts) => [...ts, { ...t, id, numero: "", x: Math.min(t.x + 8, 98), sposi: false }]); setSel(id); }} data-testid="plan-dup-table"><Copy size={14} className="mr-1" /> Duplica</Button>}
        </div>
        {t ? <TableForm t={t} set={(p) => set(t.id, p)} remove={() => { onChange((ts) => ts.filter((x) => x.id !== t.id)); setSel(null); }} /> : <div className="text-sm text-[#7B929C]">Tocca un tavolo per numerarlo e indicarne forma e misure.</div>}
        {footer}
      </div>
    </div>
  );
}
